import React, { useEffect, useState } from "react";
import { needsIosTapOverlay, subscribeHapticsEnabled } from "../../utils/haptics";

/**
 * Makes a button buzz on iOS by putting a real switch control under the finger.
 *
 * iOS never got the Vibration API, and current iOS also ignores scripted
 * toggles of the switch control; only a genuine tap on an input[switch] fires
 * the Taptic Engine. Verified on a real iPhone (2026-09-07): the haptic still
 * fires when the switch is transparent and scaled over a button, and the click
 * bubbles into the button's handler as usual.
 *
 * Host requirements: the parent carries the `ios-haptic-host` class (or
 * `position: relative; overflow: hidden` of its own) so the oversized switch
 * is clipped to the button's box. Never on a button that submits: the nearest
 * activatable element wins, so a tap landing on the switch would not submit
 * the form, and a button with no type submits when it sits inside one.
 * Renders nothing off iOS, when haptics are disabled, or when the host is
 * disabled.
 *
 * Keep it out of content that scrolls. WebKit marks touchstart and touchmove
 * on a switch as handled, so a pan that starts on the overlay never scrolls;
 * rows of overlays froze the mobile menu sheet on iPhone. That is why the
 * shared Button only renders it when the caller passes `iosTapOverlay`. Pass
 * `disabled` while the host sits in overflowing content (MobileSheet reports
 * that via `onScrollableChange`).
 */
export function IosHapticOverlay({ disabled }: { disabled?: boolean }) {
  const [active, setActive] = useState<boolean>(() => needsIosTapOverlay());
  useEffect(() => subscribeHapticsEnabled(() => setActive(needsIosTapOverlay())), []);

  if (!active || disabled) return null;
  return (
    <input
      type="checkbox"
      className="ios-haptic-overlay"
      aria-hidden="true"
      tabIndex={-1}
      // uncontrolled on purpose; the state is meaningless, only the toggle matters
      onChange={() => {}}
      {...({ switch: "" } as Record<string, string>)}
    />
  );
}

export default IosHapticOverlay;
